import type { ActiveOrdersData, SellerActiveOrdersVariables } from './types'
import { QueryOptions } from '@/resources/types'
import { OrderStatus } from '@/resources/enums'
import { gql } from 'graphql-request'
import { useQuery, QueryClient } from '@/proxy/react-query'
import { client } from '@/proxy/client'

export const DEFAULT_SELLER_ACTIVE_ORDERS_VARIABLES: SellerActiveOrdersVariables =
  Object.freeze({
    search: null,
    deliveryType: null,
  })

export const ACTIVE_ORDERS = gql`
  query sellerActiveOrders($search: String, $deliveryType: Int) {
    activeOrders(search: $search, deliveryType: $deliveryType) {
      number
      date
      checkoutType
      customer {
        name
        email
        isFirstPurchase
      }
      items {
        name
        amount
        unitPrice
        variantId
        totalPrice
        image
        categoryId
        brandId
        promotion {
          promotionName
          discountId
        }
      }
      delivery {
        address {
          street
          complement
          province
          neighborhood
          city
          location {
            coordinates
          }
        }
        courier {
          email
        }
        code
        type
        deliveryDate {
          startDate
          endDate
        }
      }
      status
      note
      isEditable
      isOrderUpdatedBySeller
      editOptions {
        option
        message
      }
      totalPrice
      expiringTime
      elapsedTime
      charging {
        coupon {
          total
          code
          description
        }
        subTotal
        discount
        cashback
        change
        shippingFee
        total
        totalSamplingsPrice
      }
      payment {
        type
      }
      serviceFee {
        isHeavyBag
        total
      }
      productChangesHistory {
        originalVariantId
        originalQuantity
        finalQuantity
        replacements {
          finalVariantId
          finalQuantity
        }
      }
    }
  }
`

export const ACTIVE_ORDERS_FOR_NOTIFICATION = gql`
  query sellerActiveOrders {
    activeOrders {
      number
      status
      date
      checkoutType
      delivery {
        type
        deliveryDate {
          startDate
          endDate
        }
      }
    }
  }
`

export const ACTIVE_ORDERS_QUERY_KEY = 'useActiveOrders'

export const activeOrdersFullListQueryKey = [
  ACTIVE_ORDERS_QUERY_KEY,
  false,
] as const

export const patchActiveOrderStatusInCache = (
  queryClient: QueryClient,
  orderNumber: string,
  status: OrderStatus
): void => {
  queryClient.setQueryData<ActiveOrdersData>(
    activeOrdersFullListQueryKey,
    old => {
      if (!old?.activeOrders?.length) return old
      const index = old.activeOrders.findIndex(
        order => order.number === orderNumber
      )
      if (index === -1) return old
      const nextOrders = [...old.activeOrders]
      nextOrders[index] = {
        ...nextOrders[index],
        status,
      }
      return { ...old, activeOrders: nextOrders }
    }
  )
}

export const useActiveOrders = (
  options: QueryOptions = {},
  isNotificationOnly = false,
  variables: SellerActiveOrdersVariables = DEFAULT_SELLER_ACTIVE_ORDERS_VARIABLES
) =>
  useQuery<ActiveOrdersData>({
    queryKey: [ACTIVE_ORDERS_QUERY_KEY, isNotificationOnly, variables],
    queryFn: ({ signal }) =>
      client.request<ActiveOrdersData>({
        document: isNotificationOnly
          ? ACTIVE_ORDERS_FOR_NOTIFICATION
          : ACTIVE_ORDERS,
        signal,
        variables: isNotificationOnly ? undefined : variables,
      }),
    ...options,
  })

export const invalidateActiveOrdersQuery = async ({
  queryClient,
}: {
  queryClient: QueryClient
}) => {
  await queryClient.invalidateQueries({
    queryKey: [ACTIVE_ORDERS_QUERY_KEY],
  })
}

export type { SellerActiveOrdersVariables } from './types'
